import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Notification {
  id: string
  template_name: string
  channel: string
  recipient: string
  priority: string
  status: string
  sent_at: string
  delivered_at: string | null
  opened_at: string | null
}

interface Channel {
  id: string
  name: string
  description: string
  enabled: boolean
  sent_today: number
  delivery_rate: number
}

interface Template {
  id: string
  name: string
  category: string
  channels: string[]
  sent_count: number
  open_rate: number
  click_rate: number
}

export default function NotificationsHubPage() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [channels, setChannels] = useState<Channel[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [notifRes, channelsRes, templatesRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/notifications/'),
      fetch(API_BASE + '/notifications/channels'),
      fetch(API_BASE + '/notifications/templates'),
      fetch(API_BASE + '/notifications/statistics')
    ])
    if (notifRes.ok) setNotifications((await notifRes.json()).notifications || [])
    if (channelsRes.ok) setChannels((await channelsRes.json()).channels || [])
    if (templatesRes.ok) setTemplates((await templatesRes.json()).templates || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'normal': return 'text-gray-600'
      case 'low': return 'text-gray-400'
      default: return 'text-gray-600'
    }
  }

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return '📧'
      case 'push': return '🔔'
      case 'sms': return '📱'
      case 'slack': return '💬'
      case 'webhook': return '🔗'
      case 'in-app': return '📲'
      default: return '📨'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Notifications Hub</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Send Notification</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Sent Today</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.sent_today?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Delivery Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.delivery_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Failed</div>
              <div className="text-2xl font-bold text-red-600">{statistics.failed}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {channels.map(channel => (
            <div key={channel.id} className={`bg-white rounded-lg shadow p-4 ${!channel.enabled ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{getChannelIcon(channel.id)}</span>
                <span className="font-semibold">{channel.name}</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">{channel.sent_today.toLocaleString()}</div>
              <div className="text-xs text-gray-500">sent today</div>
              <div className="mt-2 text-sm text-green-600">{channel.delivery_rate}% delivered</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Notifications</h2></div>
            <div className="divide-y">
              {notifications.slice(0, 10).map(notif => (
                <div key={notif.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getChannelIcon(notif.channel)}</span>
                      <div>
                        <div className="font-medium">{notif.template_name}</div>
                        <div className="text-sm text-gray-500">{notif.recipient}</div>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(notif.status)}`}>{notif.status}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span className={getPriorityColor(notif.priority)}>{notif.priority}</span>
                    <span>{new Date(notif.sent_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Templates</h3>
            <div className="space-y-3">
              {templates.slice(0, 8).map(template => (
                <div key={template.id} className="p-3 bg-gray-50 rounded">
                  <div className="font-medium text-sm">{template.name}</div>
                  <div className="text-xs text-gray-500 mb-1">{template.category}</div>
                  <div className="flex gap-2 text-xs">
                    <span className="text-green-600">{template.open_rate}% open</span>
                    <span className="text-blue-600">{template.click_rate}% click</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
